import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AppointmentService from "services/AppointmentService";

export const createAppointment = createAsyncThunk(
  "appointments/createAppointment",
  async (appointmentData, { rejectWithValue }) => {
    try {
      const response = await AppointmentService.createAppointment(
        appointmentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data.data.message || "Erreur");
    }
  }
);

export const fetchAppointments = createAsyncThunk(
  "appointments/fetchAppointments",
  async (
    { page = 1, limit = 3, status, dateFrom, dateTo },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (status) params.append("status", status);
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);

      const response = await AppointmentService.getAppointments(
        params.toString()
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.data?.message || "Erreur");
    }
  }
);

export const fetchAppointment = createAsyncThunk(
  "appointments/fetchAppointment",
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await AppointmentService.getOneAppointment(
        appointmentId
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data.data.message || "Erreur");
    }
  }
);

export const updateAppointment = createAsyncThunk(
  "appointments/updateAppointment",
  async ({ id, appointmentData }, { rejectWithValue }) => {
    try {
      const response = await AppointmentService.UpdateAppointment(
        id,
        appointmentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data.data.message || "Erreur");
    }
  }
);

export const fulfillAppointment = createAsyncThunk(
  "appointments/fulfillAppointment",
  async (appointmentData, { rejectWithValue }) => {
    try {
      const response = await AppointmentService.FulfillAppointment(
        appointmentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data.data.message || "Erreur");
    }
  }
);

export const deleteAppointment = createAsyncThunk(
  "appointments/deleteAppointment",
  async (appointmentId, { rejectWithValue }) => {
    try {
      await AppointmentService.deleteAppointment(appointmentId);
      return appointmentId;
    } catch (error) {
      return rejectWithValue(error.response.data.data.message || "Erreur");
    }
  }
);
export const validateAppointment = createAsyncThunk(
  "appointments/validateAppointment",
  async (appointmentId, { rejectWithValue }) => {
    try {
      const response = await AppointmentService.validateAppointment(
        appointmentId
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data.data.message || "Erreur");
    }
  }
);

const appointmentSlice = createSlice({
  name: "appointments",
  initialState: {
    items: [],
    total: 0,
    page: 1,
    limit: 10,
    loading: false,
    error: null,
    appointment: null,
  },
  reducers: {
    showLoading: (state) => {
      state.loading = true;
    },
    hideLoading: (state, action) => {
      state.loading = false;
    },
    hideError: (state, action) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createAppointment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createAppointment.fulfilled, (state, action) => {
      state.loading = false;
      state.items.push(action.payload);
    });
    builder.addCase(createAppointment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(fetchAppointments.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchAppointments.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.data;
      state.page = action.payload.page;
      state.total = action.payload.total;
      state.limit = action.payload.limit;
    });
    builder.addCase(fetchAppointments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(updateAppointment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateAppointment.fulfilled, (state, action) => {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id
      );
      state.items[index] = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(updateAppointment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(fulfillAppointment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fulfillAppointment.fulfilled, (state, action) => {
      state.loading = false;
      state.appointment = action.payload;
    });
    builder.addCase(fulfillAppointment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(deleteAppointment.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteAppointment.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter(
        (appointment) => appointment.id !== action.payload
      );
    });
    builder.addCase(deleteAppointment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
    builder.addCase(validateAppointment.fulfilled, (state, action) => {
      const updated = action.payload;
      const index = state.items.findIndex((item) => item.id === updated.id);
      if (index !== -1) {
        state.items[index] = updated;
      }
      state.loading = false;
    });
    builder.addCase(fetchAppointment.pending, (state) => {
      state.error = null;
    });

    builder.addCase(fetchAppointment.fulfilled, (state, action) => {
      state.appointment = action.payload;
    });

    builder.addCase(fetchAppointment.rejected, (state, action) => {
      state.error = action.payload;
    });
  },
});

export default appointmentSlice.reducer;
export const { showLoading, hideLoading, hideError } = appointmentSlice.actions;
